import useAppNavigation from '@/hooks/useAppNavigation';
import { capitalize, isNil } from 'lodash-es';
import type React from 'react';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { JsonLd } from 'react-schemaorg';
import type { MedicalBusiness, MedicalOrganization } from 'schema-dts';
import { formatHourToSchemaMarkupOpeningHourSpecification } from '../../../common/formatter';
import { ROUTES } from '../../../constants/Constants';
import { lazyReactNaiveRetry } from '../../../constants/Lazy';
import type { CartSelectionType } from '../../../redux/ducks/CartDucks.types';
import type {
  PartnerAvailableInterval,
  PartnerDetailsDescriptor,
} from '../../../redux/ducks/SearchDucks.types';
import type { AppSsrFetchedDataType } from '../../../server/AppSsr';
import { getEnvironmentVariable } from '../../../utils/EnvironmentHelper/functions';
import PartnerSeoBio from './PartnerSeoBio';
import PartnerSeoCart from './PartnerSeoCart';
import PartnerSeoHeroSection from './PartnerSeoHeroSection';
import PartnerSeoMobileFooter from './PartnerSeoMobileFooter';
import PartnerSeoServices from './PartnerSeoServices';
import PartnerSeoSummary from './PartnerSeoSummary';

const PartnerSeoNextAvailable = lazyReactNaiveRetry(
  () => import('./PartnerSeoNextAvailable'),
);

const ContactConfirmationModal = lazyReactNaiveRetry(
  () => import('../../modals/ContactConfirmationModal'),
);
const AvailableServicesModal = lazyReactNaiveRetry(
  () => import('../../modals/AvailableServicesModal'),
);
const AvailableBusinessesModal = lazyReactNaiveRetry(
  () => import('../../modals/AvailableBusinessesModal'),
);
const AvailableAppointmentsModal = lazyReactNaiveRetry(
  () => import('../../modals/AvailableAppointmentsModal'),
);

interface PartnerSeoPageProps {
  ssrFetchedData: AppSsrFetchedDataType;
  partnerProfile: PartnerDetailsDescriptor;
  partnerAvailableIntervals: PartnerAvailableInterval[];
  selectedBusiness: string | undefined;
  selectedService: string | undefined;
  selectedInterval: number | undefined;
  currentCart: CartSelectionType | undefined;
}

const PartnerSeoPage: React.FC<PartnerSeoPageProps> = ({
  ssrFetchedData,
  partnerProfile,
  partnerAvailableIntervals,
  selectedBusiness,
  selectedService,
  selectedInterval,
  currentCart,
}) => {
  const { t } = useTranslation();
  const { toLocalizedRoute } = useAppNavigation();
  const [onClient, setOnClient] = useState<boolean>(false);
  const partnerTags = useMemo<string[]>(() => {
    let result: string[];
    if (!isNil(partnerProfile)) {
      result = partnerProfile.services
        .filter((s) => !isNil(s?.category))
        .map((s) => s?.category || '');
    } else {
      result = [];
    }

    return result.filter((item, index) => result.indexOf(item) === index);
  }, [partnerProfile]);

  const partnerRoute = useMemo<string>(() => {
    return toLocalizedRoute(
      ROUTES.PARTNER_PROFILE_ROUTE.replace(
        ':partnerPath',
        `${partnerProfile.path}`,
      ),
    );
  }, [partnerProfile, toLocalizedRoute]);

  useEffect(() => {
    setOnClient(true);

    return () => {
      setOnClient(false);
    };
  }, []);

  return (
    <>
      {onClient && (
        <>
          <Suspense fallback={<div className="hidden">&nbsp;</div>}>
            <AvailableAppointmentsModal />
          </Suspense>
          <Suspense fallback={<div className="hidden">&nbsp;</div>}>
            <AvailableBusinessesModal />
          </Suspense>
          <Suspense fallback={<div className="hidden">&nbsp;</div>}>
            <AvailableServicesModal />
          </Suspense>
          <Suspense fallback={<div className="hidden">&nbsp;</div>}>
            <ContactConfirmationModal />
          </Suspense>
        </>
      )}
      <div className="mx-2 flex flex-col items-center justify-center md:mx-6 xl:mx-10">
        <div className="py-3 flex min-w-full flex-row items-center justify-between sticky top-20 bg-white z-20">
          <div className="mr-4 flex grow flex-col">
            <div className="flex min-w-full flex-row items-center justify-start gap-2 text-left text-xl font-bold sm:text-2xl">
              {!isNil(partnerProfile?.title) &&
                !t(`static.generic.gender_${partnerProfile?.title}`).includes(
                  'static.generic.gender_',
                ) && (
                  <span>
                    {t(`static.generic.gender_${partnerProfile?.title}`)}
                  </span>
                )}
              <div className="line-clamp-1 grow overflow-hidden text-ellipsis break-all capitalize">
                <h1>{`${partnerProfile?.first_name} ${partnerProfile?.last_name}`}</h1>
              </div>
            </div>
            {partnerTags && partnerTags.length > 0 && (
              <div>
                <h2 className="line-clamp-1 min-w-full overflow-hidden text-ellipsis break-all text-sm font-semibold sm:text-base">
                  {partnerTags
                    .map((tag) => capitalize(t(`static.tags.${tag}`)))
                    .join(' · ')}
                </h2>
              </div>
            )}
          </div>
          <hr className="mt-4 h-px border-b border-slate-200" />
        </div>
        <div className="mt-6 flex min-w-full flex-col items-center justify-center">
          <PartnerSeoHeroSection
            partnerProfile={partnerProfile}
            selectedBusiness={selectedBusiness}
          />
        </div>
        <div className="flex mt-6 min-w-full flex-row gap-2 pb-[64px]">
          <div className="grow pr-0 md:pr-6">
            <div className="pb-8 flex min-h-80 min-w-full flex-col gap-2">
              <PartnerSeoSummary
                ssrFetchedData={ssrFetchedData}
                partnerBusinesses={partnerProfile.businesses}
                selectedBusiness={selectedBusiness}
              />
              <hr className="mt-4 h-px border-b border-slate-200" />
              <PartnerSeoServices
                ssrFetchedData={ssrFetchedData}
                partnerProfile={partnerProfile}
                selectedService={selectedService}
                selectedBusiness={selectedBusiness}
                partnerServices={partnerProfile.services}
              />
              {onClient && (
                <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                  <PartnerSeoNextAvailable
                    ssrFetchedData={ssrFetchedData}
                    partnerProfile={partnerProfile}
                    partnerAvailableIntervals={partnerAvailableIntervals}
                    selectedInterval={selectedInterval}
                    selectedService={selectedService}
                    selectedBusiness={selectedBusiness}
                  />
                </Suspense>
              )}
              {partnerProfile?.biography && (
                <>
                  <hr className="mt-4 h-px border-b border-slate-200" />
                  <PartnerSeoBio biography={partnerProfile.biography} />
                </>
              )}
            </div>
          </div>
          <div className="flex-col relative hidden md:flex">
            <PartnerSeoCart currentCart={currentCart} />
          </div>
        </div>
        <div className="fixed bottom-0 z-20 block md:hidden">
          <PartnerSeoMobileFooter currentCart={currentCart} />
        </div>
        <JsonLd<MedicalOrganization>
          // https://www.danielkcheung.com/scheming-schemas/multi-location-business-schema-markup/
          item={{
            '@context': 'https://schema.org',
            '@type': 'MedicalOrganization',
            name: `${partnerProfile.first_name} ${partnerProfile.last_name}`,
            url: `${!import.meta.env.PROD ? 'http' : 'https'}://${
              ssrFetchedData.domain
            }${partnerRoute}`,
            '@id': `${!import.meta.env.PROD ? 'http' : 'https'}://${
              ssrFetchedData.domain
            }/partner/${partnerProfile.id}`,
            image: !isNil(partnerProfile?.media?.profile_picture)
              ? `${
                  !import.meta.env.PROD ? 'http' : 'https'
                }://${getEnvironmentVariable('REACT_APP_CDN_API')}${
                  partnerProfile?.media?.profile_picture
                }`
              : undefined,
            department: [
              ...partnerProfile.businesses
                .filter((b) => b.active && b.services.length > 0)
                .map((targetBusiness) => {
                  const businessDescriptor: MedicalBusiness = {
                    '@type': 'MedicalBusiness',
                    '@id': `${!import.meta.env.PROD ? 'http' : 'https'}://${
                      ssrFetchedData.domain
                    }/business/${targetBusiness.id}`,
                    name: `${partnerProfile.first_name} ${partnerProfile.last_name}`,
                    url: `${!import.meta.env.PROD ? 'http' : 'https'}://${
                      ssrFetchedData.domain
                    }${partnerRoute}`,
                    image: !isNil(partnerProfile?.media?.profile_picture)
                      ? `${
                          !import.meta.env.PROD ? 'http' : 'https'
                        }://${getEnvironmentVariable('REACT_APP_CDN_API')}${
                          partnerProfile?.media?.profile_picture
                        }`
                      : undefined,
                    address: !isNil(targetBusiness?.location)
                      ? {
                          '@type': 'PostalAddress',
                          addressCountry: targetBusiness.location.country,
                          postalCode: targetBusiness.location?.zipcode,
                          addressLocality: targetBusiness.location?.city,
                          addressRegion: targetBusiness.location?.state,
                          streetAddress: targetBusiness.location.address,
                        }
                      : undefined,
                    areaServed: !isNil(targetBusiness?.location)
                      ? {
                          '@type': 'City',
                          name: targetBusiness.location.city,
                        }
                      : undefined,
                    hasPOS: !isNil(targetBusiness?.location)
                      ? {
                          '@type': 'Place',
                          '@id': `${
                            !import.meta.env.PROD ? 'http' : 'https'
                          }://${ssrFetchedData.domain}/place/${
                            targetBusiness.id
                          }`,
                          geo: {
                            '@type': 'GeoCoordinates',
                            latitude: targetBusiness.location.latitude,
                            longitude: targetBusiness.location.longitude,
                          },
                        }
                      : undefined,
                    location: !isNil(targetBusiness?.location)
                      ? {
                          '@type': 'Place',
                          '@id': `${
                            !import.meta.env.PROD ? 'http' : 'https'
                          }://${ssrFetchedData.domain}/place/${
                            targetBusiness.id
                          }`,
                          geo: {
                            '@type': 'GeoCoordinates',
                            latitude: targetBusiness.location.latitude,
                            longitude: targetBusiness.location.longitude,
                          },
                        }
                      : undefined,
                    openingHoursSpecification: targetBusiness?.hours
                      ? [
                          ...targetBusiness.hours.map((h) =>
                            formatHourToSchemaMarkupOpeningHourSpecification(h),
                          ),
                        ]
                      : undefined,
                  };

                  return businessDescriptor;
                }),
            ],
          }}
        />
      </div>
    </>
  );
};

export default PartnerSeoPage;
